import React, { useState } from 'react';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../ui/button';
import {
  Type, Square, Circle, Image, Trash2, Eye, EyeOff,
  ChevronUp, ChevronDown, DollarSign, MapPin, Copy,
  Hash, User, Phone, Mail, Calendar, Home, Ruler, Tag,
  GripVertical, Clock,
} from 'lucide-react';

const LAYER_ICONS = {
  text: Type,
  rect: Square,
  circle: Circle,
  image: Image,
};

const PLACEHOLDERS = [
  { key: '{{price}}', label: 'Price', icon: DollarSign, default: '$999,000' },
  { key: '{{address}}', label: 'Full Address', icon: MapPin, default: '123 Main St, City, ST' },
  { key: '{{street}}', label: 'Street', icon: MapPin, default: '123 Main St' },
  { key: '{{city}}', label: 'City', icon: MapPin, default: 'City' },
  { key: '{{state}}', label: 'State', icon: MapPin, default: 'ST' },
  { key: '{{zip}}', label: 'Zip', icon: Hash, default: '00000' },
  { key: '{{city_state}}', label: 'City, State', icon: MapPin, default: 'City, ST' },
  { key: '{{beds}}', label: 'Beds', icon: Home, default: '4' },
  { key: '{{baths}}', label: 'Baths', icon: Home, default: '3' },
  { key: '{{sqft}}', label: 'Sq Ft', icon: Ruler, default: '2,500' },
  { key: '{{agent_name}}', label: 'Agent Name', icon: User, default: 'Agent Name' },
  { key: '{{agent_phone}}', label: 'Agent Phone', icon: Phone, default: '(555) 123-4567' },
  { key: '{{agent_email}}', label: 'Agent Email', icon: Mail, default: 'agent@email.com' },
  { key: '{{open_house_date}}', label: 'Open House', icon: Calendar, default: 'Sat, Jan 1' },
  { key: '{{mls_number}}', label: 'MLS #', icon: Hash, default: 'MLS12345' },
  { key: '{{property_type}}', label: 'Type', icon: Tag, default: 'Single Family' },
  { key: '{{year_built}}', label: 'Year Built', icon: Calendar, default: '2020' },
  { key: '{{lot_size}}', label: 'Lot Size', icon: Ruler, default: '0.25 acres' },
];

/* ── Sortable Layer Item ── */
const SortableLayerItem = ({ layer, isSelected, onSelect, onMoveUp, onMoveDown, onToggleVis, onDuplicate, onRemove, isFirst, isLast }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: layer.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const Icon = LAYER_ICONS[layer.type] || Square;
  const isPlaceholder = layer.content && /\{\{.+\}\}/.test(layer.content);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1 p-1.5 rounded cursor-pointer transition-colors text-xs group ${
        isSelected ? 'bg-purple-500/15 border border-purple-500/30' : 'bg-muted/10 border border-transparent hover:bg-muted/20'
      }`}
      onClick={() => onSelect(layer.id)}
      data-testid={`layer-item-${layer.id}`}
    >
      {/* Drag handle */}
      <div
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground/80 touch-none"
        {...attributes} {...listeners}
        data-testid={`layer-drag-${layer.id}`}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>

      {/* Icon */}
      <div
        className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: layer.type === 'text' ? '#6366f1' : (layer.fill || '#8B5CF6'), opacity: layer.opacity }}
      >
        <Icon className="h-3 w-3 text-white" />
      </div>

      {/* Label */}
      <span className="flex-1 min-w-0 truncate">
        {layer.content || layer.type.charAt(0).toUpperCase() + layer.type.slice(1)}
        {isPlaceholder && <span className="ml-1 text-[9px] text-purple-400">(field)</span>}
      </span>

      {/* Time badge */}
      {(layer.start !== undefined || layer.end !== undefined) && (
        <span className="flex-shrink-0 flex items-center gap-0.5 text-[9px] text-muted-foreground bg-muted/30 px-1 py-0.5 rounded" data-testid={`layer-time-${layer.id}`}>
          <Clock className="h-2.5 w-2.5" />
          {layer.start ?? 0}s-{layer.end == null ? '∞' : layer.end >= 999 ? '∞' : `${layer.end}s`}
        </span>
      )}

      {/* Actions */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={(e) => { e.stopPropagation(); onToggleVis(layer.id); }}
          data-testid={`layer-vis-${layer.id}`}>
          {layer.opacity > 0 ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
        </Button>
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={(e) => { e.stopPropagation(); onDuplicate(layer.id); }}
          data-testid={`layer-dup-${layer.id}`}>
          <Copy className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="icon" className="h-5 w-5" disabled={isFirst}
          onClick={(e) => { e.stopPropagation(); onMoveUp(layer.id); }}
          data-testid={`layer-up-${layer.id}`}>
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="icon" className="h-5 w-5" disabled={isLast}
          onClick={(e) => { e.stopPropagation(); onMoveDown(layer.id); }}
          data-testid={`layer-down-${layer.id}`}>
          <ChevronDown className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="icon" className="h-5 w-5 text-destructive" onClick={(e) => { e.stopPropagation(); onRemove(layer.id); }}
          data-testid={`layer-del-${layer.id}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

export const LayerPanel = ({ layers, selectedId, onSelect, onLayersChange, propertyData }) => {
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const addLayer = (type, defaults = {}) => {
    const id = `layer_${Date.now().toString(36)}`;
    const base = {
      id, type,
      x: 100, y: 100, width: 300, height: 80,
      rotation: 0, opacity: 1.0,
      start: 0, end: 999,
      content: '', fontSize: 48, fontColor: 'white', bgColor: '',
      textAlign: 'center', lineHeight: 1.2,
      shadow: {}, bgHighlight: {},
      fill: '#8B5CF6', stroke: '', strokeWidth: 0, borderRadius: 0,
      imageUrl: '',
    };
    onLayersChange([...layers, { ...base, ...defaults }]);
    onSelect(id);
  };

  const addPlaceholderLayer = (ph) => {
    addLayer('text', {
      content: ph.key,
      x: 40, y: 100 + layers.length * 80,
      width: 400, height: 70,
      fontSize: 44, fontColor: 'white', bgColor: 'black@0.6',
    });
    setShowPlaceholders(false);
  };

  const removeLayer = (id) => {
    onLayersChange(layers.filter(l => l.id !== id));
    if (selectedId === id) onSelect(null);
  };

  // "Up" in the reversed UI = move to higher index in original array (brings layer forward/on top)
  const moveLayerUp = (id) => {
    const idx = layers.findIndex(l => l.id === id);
    if (idx >= layers.length - 1) return;
    const arr = [...layers];
    [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    onLayersChange(arr);
  };

  // "Down" in the reversed UI = move to lower index in original array (sends layer backward/below)
  const moveLayerDown = (id) => {
    const idx = layers.findIndex(l => l.id === id);
    if (idx <= 0) return;
    const arr = [...layers];
    [arr[idx], arr[idx - 1]] = [arr[idx - 1], arr[idx]];
    onLayersChange(arr);
  };

  const toggleVisibility = (id) => {
    onLayersChange(layers.map(l =>
      l.id === id ? { ...l, opacity: l.opacity > 0 ? 0 : 1 } : l
    ));
  };

  const duplicateLayer = (id) => {
    const src = layers.find(l => l.id === id);
    if (!src) return;
    const newId = `layer_${Date.now().toString(36)}`;
    onLayersChange([...layers, { ...src, id: newId, x: src.x + 20, y: src.y + 20 }]);
    onSelect(newId);
  };

  // Display in reverse (last in array = top of list = highest z-order)
  const displayLayers = [...layers].reverse();
  const displayIds = displayLayers.map(l => l.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Convert from display (reversed) indices back to original array
    const oldDisplayIdx = displayLayers.findIndex(l => l.id === active.id);
    const newDisplayIdx = displayLayers.findIndex(l => l.id === over.id);

    // Reorder the display array, then reverse back to get original order
    const reordered = arrayMove(displayLayers, oldDisplayIdx, newDisplayIdx);
    onLayersChange([...reordered].reverse());
  };

  return (
    <div className="border border-border/50 rounded-lg bg-card p-3" data-testid="layer-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">Layers</h3>
      </div>

      {/* Add shape buttons */}
      <div className="flex flex-wrap gap-1 mb-2">
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => addLayer('text', { content: 'Text', width: 300, height: 60 })} data-testid="add-text-layer">
          <Type className="h-3 w-3" /> Text
        </Button>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => addLayer('rect', { width: 300, height: 80, fill: '#8B5CF6' })} data-testid="add-rect-layer">
          <Square className="h-3 w-3" /> Rect
        </Button>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => addLayer('circle', { width: 100, height: 100, fill: '#8B5CF6' })} data-testid="add-circle-layer">
          <Circle className="h-3 w-3" /> Circle
        </Button>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => addLayer('rect', { width: 300, height: 60, fill: '#8B5CF6', borderRadius: 16 })} data-testid="add-rounded-layer">
          <Square className="h-3 w-3" /> Rounded
        </Button>
      </div>

      {/* Placeholder field buttons */}
      <div className="mb-2">
        <Button
          variant={showPlaceholders ? 'secondary' : 'ghost'}
          size="sm"
          className="h-6 text-[11px] gap-1 w-full justify-start"
          onClick={() => setShowPlaceholders(!showPlaceholders)}
          data-testid="toggle-placeholders-btn"
        >
          <Tag className="h-3 w-3" />
          {showPlaceholders ? 'Hide Fields' : 'Add Property Field'}
        </Button>
        {showPlaceholders && (
          <div className="grid grid-cols-2 gap-1 mt-1.5 p-2 bg-muted/30 rounded-lg" data-testid="placeholder-grid">
            {PLACEHOLDERS.map(ph => (
              <Button
                key={ph.key}
                variant="ghost"
                size="sm"
                className="h-6 text-[10px] gap-1 justify-start px-1.5"
                onClick={() => addPlaceholderLayer(ph)}
                data-testid={`placeholder-${ph.key.replace(/[{}]/g, '')}`}
              >
                <ph.icon className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{ph.label}</span>
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Quick-add from property data */}
      {propertyData && (
        <div className="flex flex-wrap gap-1 mb-2 pt-2 border-t border-border/30">
          {propertyData.price && (
            <Button variant="ghost" size="sm" className="h-6 text-xs gap-1" onClick={() => addLayer('text', { content: propertyData.price, x: 40, y: 60, fontSize: 52, fontColor: '#C9A96E', bgColor: '#1E293B@0.85' })}>
              <DollarSign className="h-3 w-3" /> Price
            </Button>
          )}
          {propertyData.address && (
            <Button variant="ghost" size="sm" className="h-6 text-xs gap-1" onClick={() => addLayer('text', { content: propertyData.address, x: 40, y: 1760, fontSize: 36 })}>
              <MapPin className="h-3 w-3" /> Address
            </Button>
          )}
        </div>
      )}

      {/* Layer list with drag-and-drop */}
      <div className="space-y-1 max-h-44 overflow-y-auto editor-scroll" data-testid="layer-list">
        {layers.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">Add layers to design your reel</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={displayIds} strategy={verticalListSortingStrategy}>
              {displayLayers.map((layer, displayIdx) => (
                <SortableLayerItem
                  key={layer.id}
                  layer={layer}
                  isSelected={selectedId === layer.id}
                  onSelect={onSelect}
                  onMoveUp={moveLayerUp}
                  onMoveDown={moveLayerDown}
                  onToggleVis={toggleVisibility}
                  onDuplicate={duplicateLayer}
                  onRemove={removeLayer}
                  isFirst={displayIdx === 0}
                  isLast={displayIdx === displayLayers.length - 1}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};
